import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { auth, db, getCollectionName } from '../firebase';
import { collection, query, where, onSnapshot, getDocs } from '../firestore-compat';
import { hasPermission, CAPABILITIES } from '../config/permissions';
import {
  HeartPulse, Settings,
  Stethoscope, LayoutDashboard, Calendar, CheckSquare, FolderKanban,
  ClipboardList, Users, CalendarDays, FileText, Package,
  Handshake, UserCheck, Monitor, Upload,
  BarChart2, MapPin, Phone, Inbox, Briefcase,
  Search, Activity, TrendingUp, Building2,
  AlertCircle, ClipboardCheck, ChevronRight,
  Truck, Zap, Microscope, MessageCircle, Receipt, Send, Code, Wallet, Eye,
  TabletSmartphone, PackageCheck, ShieldAlert, Hash
} from 'lucide-react';
import '../App.css';

const Sidebar = ({ autoHide }) => {
  const location = useLocation();
  const [userRoles, setUserRoles] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [pendingInvitesCount, setPendingInvitesCount] = useState(0);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  // não lidos nas caixas de e-mail (enviado pelo AvisoEmail a cada minuto)
  const [emailNaoLidas, setEmailNaoLidas] = useState(0);
  useEffect(() => {
    const f = e => setEmailNaoLidas(Number(e.detail) || 0);
    window.addEventListener('quality-email-naolidas', f);
    return () => window.removeEventListener('quality-email-naolidas', f);
  }, []);
  const [expandedCategories, setExpandedCategories] = useState({});
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) { setLoading(false); return; }
    const usersRef = collection(db, getCollectionName('users'));
    const q = query(usersRef, where('email', '==', currentUser.email?.toLowerCase()));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const userData = snapshot.docs[0].data();
        const roles = Array.isArray(userData.roles) ? userData.roles
          : (userData.role ? [userData.role] : ['leitor']);
        setUserRoles(roles);
        setPermissions(userData.permissions || {});
      } else {
        setUserRoles(['leitor']);
        setPermissions({});
      }
      setLoading(false);
    }, () => setLoading(false));
    return () => unsubscribe();
  }, []);

  // 2026-09-26: este efeito chamava `fetchInvites()` (uma função async que criava 4
  // assinaturas onSnapshot e retornava uma função de limpeza) sem nunca capturar essa
  // limpeza — o cleanup do effect só desligava `unsubNotif`, nunca as 4 assinaturas de
  // convites. Cada vez que o efeito rodava de novo (fast refresh em dev, remount, etc.)
  // as 4 assinaturas antigas ficavam pra sempre ativas, empilhando mais 4 a cada rodada.
  // Isso foi identificado como uma das causas do estouro de egress do Supabase (~10,5
  // milhões de queries em 28h). Correção: junta num badge que checa periodicamente
  // (a cada 3min) em vez de manter 4 assinaturas em tempo real vivas, e limpa direito.
  useEffect(() => {
    if (!currentUser || loading) return;
    let cancelled = false;

    const checarConvites = async () => {
      const usersRef = collection(db, 'users');
      const snap = await getDocs(query(usersRef, where('email', '==', currentUser.email)));
      if (cancelled || snap.empty) return;
      const userData = snap.docs[0].data();
      const myAgendaName = userData.agendaName || userData.name;
      const myUid = currentUser.uid;
      // Mesma regra da tela Convites de Agenda (Convites.js): só conta o convite em que EU sou participante
      // e que ainda não aceitei, recusei nem ocultei. (Antes contava os convites de todo mundo → "99+".)
      const collectionsToWatch = [['auditorias', 'auditores'], ['agendaDirecao', 'responsaveis'], ['auditorias_homecare', 'auditores'], ['agenda_pessoal', 'participantesUids']];
      const roles = Array.isArray(userData.roles) ? userData.roles : (userData.role ? [userData.role] : []);
      const isSuperAdmin = currentUser.email === 'alexandrebareaoliveira@gmail.com' || roles.includes('admin') || roles.includes('administrador');
      const isDirector = ['Ana Paula', 'Dr. Mário', 'Diretoria'].some(d => myAgendaName && (d.includes(myAgendaName) || myAgendaName.includes(d)));
      let ignorados = {};
      try { ignorados = JSON.parse(localStorage.getItem('ignored_invites') || '{}'); } catch { /* sem armazenamento */ }
      let total = 0;
      for (const [collName, campo] of collectionsToWatch) {
        if (cancelled) return;
        if (collName === 'agendaDirecao' && isDirector) continue; // diretores veem direto na agenda
        const inviteQuery = query(collection(db, collName), where('conviteSistema', '==', true), where('status', '==', 'Agendado'));
        const inviteSnap = await getDocs(inviteQuery);
        total += inviteSnap.docs.filter(doc => {
          const data = doc.data();
          if (ignorados[doc.id]) return false;
          if (data.criadoPorUid === myUid || data.createdBy === myUid || data.ownerUid === myUid) return false;
          if (data.aceitePorUids?.includes(myUid) || data.aceitePor?.includes(myAgendaName)) return false;
          if (data.recusas?.some(r => r.uid === myUid || r.nome === myAgendaName || (r.email && r.email === currentUser.email))) return false;
          const nomes = [...(Array.isArray(data[campo]) ? data[campo] : typeof data[campo] === 'string' ? [data[campo]] : []), ...(data.participantesNomes || [])];
          const peloNome = !!myAgendaName && nomes.some(n => typeof n === 'string' && n.includes(myAgendaName));
          const peloUid = (data.participantesUids || []).includes(myUid);
          return collName === 'agenda_pessoal' || !isSuperAdmin ? peloNome || peloUid : peloNome;
        }).length;
      }
      if (!cancelled) setPendingInvitesCount(total);
    };

    checarConvites();
    const interval = setInterval(checarConvites, 180000);

    const qNotif = query(collection(db, 'notificacoes'), where('destinatarioUid', '==', currentUser.uid), where('lida', '==', false));
    const unsubNotif = onSnapshot(qNotif, (snapshot) => setUnreadNotificationsCount(snapshot.docs.length));

    return () => { cancelled = true; clearInterval(interval); unsubNotif(); };
  }, [currentUser, loading]);

  const canAccess = (key) => {
    if (!key) return true;
    if (Array.isArray(key)) return key.some(k => canAccess(k)); // item com mais de um módulo: basta um

    const isSuperAdmin = currentUser?.email === 'alexandrebareaoliveira@gmail.com';
    if (isSuperAdmin || userRoles.includes('admin') || userRoles.includes('administrador')) return true;
    if (key === 'agenda_pessoal' || key === 'aulas') return true;
    if (key === 'admin_tutoriais' && (permissions?.configuracoes?.view === true || permissions?.admin_tutoriais?.view === true)) return true;
    const keyToCapability = {
      'dashboard': CAPABILITIES.VIEW_DASHBOARD,
      'agenda_direcao': CAPABILITIES.VIEW_AGENDA_DIRECAO,
      'projeto_direcao': CAPABILITIES.VIEW_PROJETO_DIRECAO,
      'agenda_qualidade': CAPABILITIES.VIEW_AGENDA_QUALIDADE,
      'agenda_homecare': CAPABILITIES.VIEW_AGENDA_HOMECARE,
      'projeto_homecare': CAPABILITIES.VIEW_PROJETO_HOMECARE,
      'tarefas_homecare': CAPABILITIES.VIEW_TAREFAS_HOMECARE,
      'tarefas_qualidade': CAPABILITIES.VIEW_TAREFAS_QUALIDADE,
      'indicadores': CAPABILITIES.VIEW_INDICADORES,
      'projeto_iw': CAPABILITIES.VIEW_PROJETO_IW,
      'analysis': CAPABILITIES.VIEW_ANALYSIS,
      'admin_tutoriais': CAPABILITIES.VIEW_ADMIN_TUTORIAIS,
      'abrir_chamado': CAPABILITIES.VIEW_ABRIR_CHAMADO,
      'meus_chamados': CAPABILITIES.VIEW_MEUS_CHAMADOS,
      'painel_chamados': CAPABILITIES.VIEW_PAINEL_CHAMADOS,
      'dashboard_planilha': CAPABILITIES.VIEW_DASHBOARD,
      'escalas': CAPABILITIES.VIEW_GESTAO_ESCALAS,
      'escalas_enfermeiros': CAPABILITIES.VIEW_ESCALAS_ENFERMEIROS,
      'portal_cooperativa': CAPABILITIES.VIEW_PORTAL_COOPERATIVA,
      'avaliacao_rh': CAPABILITIES.VIEW_AVALIACAO_RH,
      'acessos_ti': CAPABILITIES.VIEW_ACESSOS_TI,
      'estoque_controle': CAPABILITIES.VIEW_ESTOQUE_CONTROLE,
      'estoque_solicitacao': CAPABILITIES.VIEW_ESTOQUE_SOLICITACAO,
      'estoque_consumo': CAPABILITIES.VIEW_ESTOQUE_CONSUMO,
      'configuracoes': CAPABILITIES.MANAGE_SETTINGS,
      'html_viewer': CAPABILITIES.VIEW_HTML_VIEWER,
      'aulas': CAPABILITIES.VIEW_AULAS,
      'convites': CAPABILITIES.VIEW_CONVITES,
      'meu_projeto': CAPABILITIES.VIEW_MEU_PROJETO,
      'minhas_tarefas': CAPABILITIES.VIEW_MINHAS_TAREFAS,
      'gestao_pacientes': CAPABILITIES.VIEW_GESTAO_PACIENTES,
      'formulario_passagem': CAPABILITIES.VIEW_FORMULARIO_PASSAGEM,
      'gestao_passagens': CAPABILITIES.VIEW_GESTAO_PASSAGENS,
      'ocorrencias_supervisor': CAPABILITIES.VIEW_OCORRENCIAS_SUPERVISOR,
      'ocorrencias_setores': CAPABILITIES.VIEW_OCORRENCIAS_SETORES,
      'gestao_ocorrencias': CAPABILITIES.VIEW_GESTAO_OCORRENCIAS,
    };
    const capability = keyToCapability[key];
    if (capability && hasPermission(userRoles, capability, permissions)) return true;
    if (permissions[key]?.view === true) return true;
    return false;
  };

  const toggleCategory = (cat) => {
    setExpandedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  // Menu em seções, no visual do INEP: ícone colorido (emoji) no primeiro nível, ícone de linha nos subitens.
  // "secao" agrupa os itens sob um título (Visão geral, Gestão, Plataforma).
  const categories = [
    { secao: 'Visão geral', id: 'home', name: 'Início', emoji: '🏠', path: '/home', permissionKey: 'dashboard' },
    { secao: 'Visão geral', id: 'agenda_pessoal', name: 'Minha Agenda', emoji: '📅', path: '/minha-agenda', permissionKey: 'agenda_pessoal' },
    { secao: 'Visão geral', id: 'email', name: 'E-mail', emoji: '✉️', path: '/email', permissionKey: 'email' },
    // Convites para compromissos (aceitar/recusar) e avisos do sistema — não são e-mails
    { secao: 'Visão geral', id: 'convites', name: 'Convites de Agenda', emoji: '📨', path: '/convites', permissionKey: 'convites', badge: true },
    // Atendimento por WhatsApp (QR code) — antes "Teste Jarvis"
    { secao: 'Visão geral', id: 'teste_jarvis', name: 'Zaping', emoji: '💬', path: '/zaping', permissionKey: 'teste_jarvis' },
    {
      secao: 'Gestão', id: 'homecare', name: 'Homecare', emoji: '🏥',
      subItems: [
        { name: 'Gestão Captação',                      path: '/gestao-captacao',     Icon: AlertCircle,    permissionKey: 'gestao_captacao' },
        { name: 'Agenda Enf. Captação',                 path: '/agenda-captacao',     Icon: CalendarDays,   permissionKey: 'agenda_captacao' },
        { name: 'Painel Implantados',                   path: '/painel-implantados',  Icon: CheckSquare,    permissionKey: 'painel_implantados' },
        { name: 'Minhas Ocorrências',                   path: '/ocorrencias-setores', Icon: ClipboardCheck, permissionKey: 'ocorrencias_setores' },
        { name: 'Gestão Ocorrências',                   path: '/gestao-ocorrencias',  Icon: ClipboardList,  permissionKey: 'gestao_ocorrencias' },
        { name: 'Agenda Homecare',                      path: '/agenda-homecare',     Icon: Calendar,       permissionKey: 'agenda_homecare' },
        { name: 'Projeto Homecare',                     path: '/projeto-homecare',    Icon: FolderKanban,   permissionKey: 'projeto_homecare' },
        { name: 'Projeto IW',                           path: '/app',                 Icon: Briefcase,      permissionKey: 'projeto_iw' },
        { name: 'Cronograma IW',                        path: '/cronograma-iw',       Icon: CalendarDays,   permissionKey: 'cronograma_iw' },
        { name: 'Cadastros de Pacientes',               path: '/gestao-pacientes',    Icon: Users,          permissionKey: 'gestao_pacientes' },
        { name: 'Escalas Técnicos',                     path: '/escalas',             Icon: ClipboardList,  permissionKey: 'gestao_escalas' },
        { name: 'Form. Passagem Téc. Educação',         path: '/formulario-passagem', Icon: FileText,       permissionKey: 'formulario_passagem' },
        { name: 'Gestão Passagens',                     path: '/gestao-passagens',    Icon: BarChart2,      permissionKey: 'gestao_passagens' },
        { name: 'Portal Cooperativa',                   path: '/portal-cooperativa',  Icon: Handshake,      permissionKey: 'portal_cooperativa' },
        { name: 'Liberação de Acesso RH',               path: '/portal-rh',           Icon: UserCheck,      permissionKey: 'avaliacao_rh' },
        { name: 'Criação de Acessos IW',                path: '/portal-ti',           Icon: Monitor,        permissionKey: 'acessos_ti' },
        { name: 'Gestão de Remoções',                   path: '/gestao-remocoes',                   Icon: Truck,          permissionKey: 'gestao_remocoes' },
        { name: 'Prévia de Recargas',                   path: '/gestao-previa-recargas',            Icon: Zap,            permissionKey: 'previa_recargas' },
        { name: 'Dispositivos',                         path: '/controle-dispositivos',             Icon: TabletSmartphone, permissionKey: ['controle_dispositivos', 'solicitacao_troca'] },
        { name: 'Liberação dos Kits',                   path: '/liberacao-kits',                    Icon: PackageCheck,   permissionKey: 'liberacao_kits' },
        { name: 'Exames',                               path: '/gestao-solicitacao-exames',         Icon: Microscope,     permissionKey: 'solicitacao_exames' },
        { name: 'Monitoramento Clínico',                path: '/gestao-monitoramento-clinico',      Icon: Activity,       permissionKey: 'monitoramento_clinico' },
        { name: 'Envio ao Carefy',                      path: '/envio-carefy',                      Icon: Send,           permissionKey: 'envio_carefy' },
        { name: 'Gestão de Feridas',                    path: '/gestao-feridas',                    Icon: HeartPulse,     permissionKey: 'gestao_feridas' },
        { name: 'Ouvidorias',                           path: '/gestao-ouvidorias',                 Icon: MessageCircle,  permissionKey: 'gestao_ouvidorias' },
        { name: 'Ouvidoria Monitoramento',              path: '/ouvidoria-monitoramento',           Icon: Eye,            permissionKey: 'ouvidoria_monitoramento' },
        { name: 'Regras de Contrato',                   path: '/regras-contrato',                   Icon: FileText,       permissionKey: 'regras_contrato' },
        { name: 'Huddle Homecare',                      path: '/huddle-homecare',                   Icon: Users,          permissionKey: 'huddle_homecare' },
        { name: 'Controle de Notas',                    path: '/gestao-controladoria',              Icon: Wallet,         permissionKey: 'gestao_controladoria' },
        { name: 'Gestão da Logística',                  path: '/gestao-logistica',                  Icon: Package,        permissionKey: 'gestao_logistica' },
      ]
    },
    {
      secao: 'Gestão', id: 'dashboards', name: 'Dashboard', emoji: '📊',
      subItems: [
        { name: 'Dashboard Hub',                          path: '/dashboard-hub',             Icon: LayoutDashboard, permissionKey: 'dashboard_hub' },
        { name: 'Dashboard Captação',                     path: '/dashboard-implantacao',    Icon: FolderKanban,    permissionKey: 'dashboard_implantacao' },
        { name: 'Dashboard Hospitalização',               path: '/dashboard-hospitalizacao', Icon: HeartPulse,      permissionKey: 'dashboard_hospitalizacao' },
        { name: 'Dashboard Feridas',                      path: '/dashboard-feridas',        Icon: Stethoscope,     permissionKey: 'dashboard_feridas' },
        { name: 'Controle de Infecção',                   path: '/painel-infeccao',          Icon: ShieldAlert,     permissionKey: 'painel_infeccao' },
        { name: 'Dashboard Projeto IW',                   path: '/analysis',                 Icon: Activity,        permissionKey: 'analysis' },
        { name: 'Uploads',                                path: '/uploads',                  Icon: Upload,          permissionKey: 'uploads' },
        { name: 'Censo Homecare',                         path: '/dashboard-indicadores',    Icon: BarChart2,       permissionKey: 'dashboard_indicadores' },
        { name: 'Dashboard Telefonia',                    path: '/dashboard-planilha',       Icon: Phone,           permissionKey: 'dashboard_planilha' },
        { name: 'Telefonia ao Vivo',                      path: '/telefonia-ao-vivo',        Icon: Phone,           permissionKey: ['telefonia_ao_vivo', 'dashboard_planilha'] },
        { name: 'Acomp. Visitas Domiciliares',            path: '/dashboard-visitas',        Icon: MapPin,          permissionKey: 'dashboard_visitas' },
        { name: 'Dashboard Prontuários',                  path: '/dashboard-prontuario',     Icon: FileText,        permissionKey: 'dashboard_prontuario' },
        { name: 'Gestão de Orçamentos',                    path: '/gestao-orcamentos',             Icon: Receipt,     permissionKey: 'gestao_orcamentos' },
        { name: 'Pendência de Orçamentos',                 path: '/pendencia-orcamentos',          Icon: Receipt,     permissionKey: 'pendencia_orcamentos' },
        { name: 'Envio de Contas',                         path: '/gestao-contas-enviadas',        Icon: Send,        permissionKey: 'contas_enviadas' },
        { name: 'Visitas × Produtividade',                 path: '/gestao-visitas-produtividade',  Icon: TrendingUp,  permissionKey: 'visitas_produtividade' },
      ]
    },
    {
      secao: 'Gestão', id: 'qualidade', name: 'Qualidade', emoji: '🛡️',
      subItems: [
        { name: 'Agenda',             path: '/agenda-qualidade',     Icon: Calendar,   permissionKey: 'agenda_qualidade' },
        { name: 'Indicadores de Agenda', path: '/indicador-agenda',   Icon: TrendingUp, permissionKey: 'indicador_agenda' },
      ]
    },
    {
      secao: 'Gestão', id: 'direcao', name: 'Direção', emoji: '⭐',
      subItems: [
        { name: 'Agenda Direção', path: '/agenda-direcao',  Icon: Calendar,      permissionKey: 'agenda_direcao' },
        { name: 'Projeto Direção',path: '/projeto-direcao', Icon: FolderKanban,  permissionKey: 'projeto_direcao' },
        { name: 'Obras Direção',  path: '/obras-direcao',   Icon: Building2,     permissionKey: 'obras_direcao' },
        // Demonstração estática (public/apresentacao-convenio) — só dados fictícios; abre em nova aba
        { name: 'Apresentação pro Convênio', href: '/apresentacao-convenio/index.html#/home', Icon: Monitor, permissionKey: 'apresentacao_convenio' },
      ]
    },
    {
      secao: 'Plataforma', id: 'servicos', name: 'Suporte', emoji: '🛠️',
      subItems: [
        { name: 'Abrir Chamado',        path: '/abrir-chamado',     Icon: Hash,        permissionKey: 'abrir_chamado' },
        { name: 'Meus Chamados',        path: '/meus-chamados',     Icon: Inbox,       permissionKey: 'meus_chamados' },
        { name: 'Painel Sustentação',   path: '/painel-chamados',   Icon: Monitor,     permissionKey: 'painel_chamados' },
        { name: 'Tarefas da Qualidade', path: '/tarefas-qualidade', Icon: CheckSquare, permissionKey: 'tarefas_qualidade' },
      ]
    },
    { secao: 'Plataforma', id: 'inep', name: 'INEP — Ensino', emoji: '🎓', path: '/inep', permissionKey: 'inep_ensino' },
    {
      secao: 'Plataforma', id: 'admin', name: 'Administração', emoji: '⚙️',
      subItems: [
        { name: 'Auditoria',          path: '/audit',                 Icon: Search,        emailRestrict: 'alexandrebareaoliveira@gmail.com' },
        { name: 'Eventos',            path: '/admin-eventos',         Icon: CalendarDays,  emailRestrict: 'alexandrebareaoliveira@gmail.com' },
        { name: 'Histórico de acessos', path: '/historico-acessos', Icon: Eye,          emailRestrict: 'alexandrebareaoliveira@gmail.com' },
        { name: 'Visualizador HTML',  path: '/html-viewer',           Icon: Code,          permissionKey: 'html_viewer' },
        { name: 'Configurações',      path: '/settings',              Icon: Settings,      permissionKey: 'configuracoes' },
      ]
    }
  ];

  const totalBadge = pendingInvitesCount + unreadNotificationsCount;
  const marca = (
    <div className="sb__brand">
      <span className="sb__mark"><img src="/favicon-64.png" alt="" width={26} height={26} /></span>
      <div className="sb__brand-txt"><b>Quality</b><small>Gestão do Grupo Prontobaby</small></div>
    </div>
  );

  if (loading) return (
    <div className={`sb ${autoHide ? 'sb--hidden' : ''}`}>{marca}</div>
  );

  // item restrito a um e-mail (Auditoria, Eventos): só para esse e-mail — não cai no canAccess (sem chave = liberado)
  const podeVerSub = sub => (sub.emailRestrict ? currentUser?.email === sub.emailRestrict : canAccess(sub.permissionKey));
  const visivel = cat => (cat.subItems ? cat.subItems.some(podeVerSub) : canAccess(cat.permissionKey));
  const secoes = [...new Set(categories.map(c => c.secao))]
    .map(titulo => ({ titulo, itens: categories.filter(c => c.secao === titulo && visivel(c)) }))
    .filter(s => s.itens.length);

  const contador = n => <span className="sb__badge">{n > 99 ? '99+' : n}</span>;

  return (
    <div className={`sb ${autoHide ? 'sb--hidden' : ''}`}>
      {marca}

      <nav className="sb__nav">
        {secoes.map(secao => (
          <div key={secao.titulo} className="sb__secao">
            <div className="sb__secao-titulo">{secao.titulo}</div>
            {secao.itens.map(cat => {
              // Link direto (sem subitens)
              if (cat.path) {
                return (
                  <NavLink key={cat.id} to={cat.path} className={({ isActive }) => `sb__item ${isActive ? 'sb__item--active' : ''}`}>
                    <span className="sb__emoji">{cat.emoji}</span>
                    <span className="sb__label">{cat.name}</span>
                    {cat.badge && totalBadge > 0 && contador(totalBadge)}
                    {cat.id === 'email' && emailNaoLidas > 0 && <span className="sb__badge" style={{ background: '#fff', color: '#00796b' }} title="E-mails não lidos">{emailNaoLidas > 999 ? '999+' : emailNaoLidas}</span>}
                  </NavLink>
                );
              }
              const isExpanded = expandedCategories[cat.id];
              const hasActiveChild = cat.subItems.some(sub => sub.path && location.pathname === sub.path);
              return (
                <div key={cat.id} className="sb__group">
                  <button className={`sb__item sb__item--group ${hasActiveChild ? 'sb__item--parent-active' : ''}`} onClick={() => toggleCategory(cat.id)}>
                    <span className="sb__emoji">{cat.emoji}</span>
                    <span className="sb__label">{cat.name}</span>
                    <ChevronRight size={14} className={`sb__chevron ${isExpanded || hasActiveChild ? 'sb__chevron--open' : ''}`} />
                  </button>
                  {(isExpanded || hasActiveChild) && (
                    <div className="sb__children">
                      {cat.subItems.filter(podeVerSub).map(sub => (sub.href ? (
                        // fora do app React (abre em nova aba)
                        <a key={sub.name} href={sub.href} target="_blank" rel="noopener noreferrer" className="sb__child">
                          <sub.Icon size={14} strokeWidth={1.8} className="sb__child-icon" />
                          <span className="sb__child-label">{sub.name}</span>
                        </a>
                      ) : (
                        <NavLink key={sub.name} to={sub.path} className={({ isActive }) => `sb__child ${isActive ? 'sb__child--active' : ''}`}>
                          <sub.Icon size={14} strokeWidth={1.8} className="sb__child-icon" />
                          <span className="sb__child-label">{sub.name}</span>
                        </NavLink>
                      )))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="sb__footer">
        <div className="sb__user-email">{currentUser?.email}</div>
      </div>
    </div>
  );
};

export default Sidebar;
